/**
 * VIVE Luxury Theme Customizations - Addons JS Module
 * Namespace: Scoped entirely to vive- addons classes.
 */

(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', () => {
    initRevealStaggerDelays();
    initScrollParallax();
    initTestimonialsSliders();
    initVideoBanners();
  });

  /**
   * 1. REVEAL STAGGER DELAY CALCULATIONS
   * Automatically assigns incrementing transition delays to items inside a stagger container.
   */
  function initRevealStaggerDelays() {
    const containers = document.querySelectorAll('.vive-reveal-stagger');
    if (containers.length === 0) return;

    containers.forEach(container => {
      const items = container.querySelectorAll('.vive-reveal-item');
      items.forEach((item, index) => {
        // Increment delay by 120ms per card to create clean luxury cascade
        item.style.setProperty('--vive-stagger-delay', `${index * 120}ms`);
      });
    });
  }

  /**
   * 2. SCROLL PARALLAX SYSTEM
   * Performs vertical translation of nested media assets.
   */
  function initScrollParallax() {
    const parallaxItems = document.querySelectorAll('.vive-parallax');
    if (parallaxItems.length === 0) return;

    // Use Intersection Observer to only target elements in the active viewport
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('vive-parallax-visible');
        } else {
          entry.target.classList.remove('vive-parallax-visible');
        }
      });
    }, { threshold: 0 });

    parallaxItems.forEach(item => observer.observe(item));

    let ticking = false;

    const performTranslation = () => {
      parallaxItems.forEach(item => {
        if (!item.classList.contains('vive-parallax-visible')) return;

        const rect = item.getBoundingClientRect();
        const speed = parseFloat(item.getAttribute('data-parallax-speed') || '0.12');
        const media = item.querySelector('img, video');

        if (media) {
          // Translate offset based on position relative to viewport top bounds
          const offset = rect.top * speed;
          media.style.transform = `translate3d(0, ${offset}px, 0)`;
        }
      });
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(performTranslation);
        ticking = true;
      }
    }, { passive: true });

    // Initial render call
    performTranslation();
  }

  /**
   * 5. CUSTOMER REVIEWS TESTIMONIALS SLIDER
   */
  function initTestimonialsSliders() {
    const sections = document.querySelectorAll('.vive-reviews-section');
    if (sections.length === 0) return;

    sections.forEach(section => {
      const slides = section.querySelectorAll('.vive-reviews__slide');
      if (slides.length <= 1) return;

      const dots = section.querySelectorAll('.vive-reviews__dot');
      const arrowPrev = section.querySelector('.vive-reviews__arrow--prev');
      const arrowNext = section.querySelector('.vive-reviews__arrow--next');

      let currentIndex = 0;
      let sliderInterval = null;
      const intervalDuration = parseInt(section.getAttribute('data-interval') || '5000', 10);

      const showSlide = (index) => {
        currentIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, idx) => {
          const isActive = idx === currentIndex;
          slide.classList.toggle('vive-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        dots.forEach((dot, idx) => {
          dot.classList.toggle('vive-active', idx === currentIndex);
          dot.setAttribute('aria-selected', idx === currentIndex ? 'true' : 'false');
        });
      };

      const nextSlide = () => showSlide(currentIndex + 1);
      const prevSlide = () => showSlide(currentIndex - 1);

      const startInterval = () => {
        if (sliderInterval) clearInterval(sliderInterval);
        sliderInterval = setInterval(nextSlide, intervalDuration);
      };

      const stopInterval = () => {
        if (sliderInterval) clearInterval(sliderInterval);
      };

      if (arrowNext) arrowNext.addEventListener('click', () => { nextSlide(); startInterval(); });
      if (arrowPrev) arrowPrev.addEventListener('click', () => { prevSlide(); startInterval(); });
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          showSlide(parseInt(dot.getAttribute('data-index') || '0', 10));
          startInterval();
        });
      });

      showSlide(currentIndex);
      startInterval();

      section.addEventListener('mouseenter', stopInterval);
      section.addEventListener('mouseleave', startInterval);
      section.addEventListener('focusin', stopInterval);
      section.addEventListener('focusout', startInterval);
    });
  }

  /**
   * 6. FEATURED VIDEO BANNER CONTROLS
   */
  function initVideoBanners() {
    const banners = document.querySelectorAll('.vive-video-banner');
    if (banners.length === 0) return;

    banners.forEach(banner => {
      const video = banner.querySelector('.vive-video-banner__video');
      const playBtn = banner.querySelector('.vive-video-banner__btn--play');
      const muteBtn = banner.querySelector('.vive-video-banner__btn--mute');

      if (!video) return;

      if (playBtn) {
        playBtn.addEventListener('click', () => {
          if (video.paused) {
            video.play();
            playBtn.innerHTML = `
              <svg viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            `;
            playBtn.setAttribute('aria-label', 'Pause Video');
          } else {
            video.pause();
            playBtn.innerHTML = `
              <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
            `;
            playBtn.setAttribute('aria-label', 'Play Video');
          }
        });
      }

      if (muteBtn) {
        muteBtn.addEventListener('click', () => {
          video.muted = !video.muted;
          if (video.muted) {
            muteBtn.innerHTML = `
              <svg viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77zM3 9v6h4l5 5V4L7 9H3z"/></svg>
            `;
            muteBtn.setAttribute('aria-label', 'Unmute Video');
          } else {
            muteBtn.innerHTML = `
              <svg viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM3 9v6h4l5 5V4L7 9H3zm16.5 3c0 2.21-1.79 4-4 4s-4-1.79-4-4 1.79-4 4-4 4 1.79 4 4z"/></svg>
            `;
            muteBtn.setAttribute('aria-label', 'Mute Video');
          }
        });
      }
    });
  }
})();
